"use client"

import * as React from "react"
import { startTransition } from "react"
import {
  AlertCircle,
  Braces,
  Check,
  Copy,
  FileJson,
  Maximize2,
} from "lucide-react"

import { toCsv, toMinifiedJson, toToml, toToon, toYaml } from "@/lib/formats"
import { JsonSchema, jsonSchema } from "@/lib/schema"
import { TemplateKey, TEMPLATES } from "@/lib/templates"
import { countTokens } from "@/lib/tokenizer"
import { FORMAT_ORDER, type FormatType } from "@/lib/types"
import { cn } from "@/lib/utils"
import { useMounted } from "@/hooks/use-mounted"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { DialogHelper } from "@/components/dialog-helper"

import { TokenViewer } from "./token-viewer"

export function AnalyzeApp() {
  const isMounted = useMounted()
  const [input, setInput] = React.useState(
    JSON.stringify(TEMPLATES.simple, null, 2)
  )
  const [parsedData, setParsedData] = React.useState<JsonSchema | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [highlightTokens, setHighlightTokens] = React.useState(true)
  const [copiedFormat, setCopiedFormat] = React.useState<FormatType | null>(
    null
  )
  const copyTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const [fullscreenFormat, setFullscreenFormat] =
    React.useState<FormatType | null>(null)

  React.useEffect(() => {
    let didCancel = false

    if (input.trim() === "") {
      setParsedData(null)
      setError(null)
      return () => {
        didCancel = true
      }
    }

    ;(async () => {
      const result = await jsonSchema.safeParseAsync(input)
      if (didCancel) return
      if (result.success) {
        setParsedData(result.data)
        setError(null)
      } else {
        setError(result.error.issues[0].message)
        setParsedData(null)
      }
    })()

    return () => {
      didCancel = true
    }
  }, [input])

  const [formats, setFormats] = React.useState<{
    minified: string
    yaml: string
    toml: string
    CSV: string
    toon: string
  } | null>(null)
  const [stats, setStats] = React.useState<Array<{
    format: FormatType
    count: number
    diff: number
    percent: number
    isBaseline: boolean
  }> | null>(null)

  React.useEffect(() => {
    if (!parsedData || !isMounted) {
      setFormats(null)
      setStats(null)
      return
    }

    let didCancel = false

    const processData = async () => {
      try {
        const [yaml, toml, csv, toon] = await Promise.all([
          toYaml(parsedData),
          toToml(parsedData),
          toCsv(parsedData),
          toToon(parsedData),
        ])

        if (didCancel) return

        const minified = toMinifiedJson(parsedData)
        const formatsData = {
          minified,
          yaml,
          toml,
          CSV: csv,
          toon,
        }

        startTransition(() => {
          setFormats(formatsData)
        })

        const tokenCounts: Record<string, number> = {}

        const processTokenCount = async (text: string): Promise<number> => {
          if (didCancel) return 0
          await new Promise((resolve) => setTimeout(resolve, 0))
          if (didCancel) return 0
          return countTokens(text)
        }

        tokenCounts.minified = await processTokenCount(minified)
        if (didCancel) return

        tokenCounts.yaml = await processTokenCount(yaml)
        if (didCancel) return

        tokenCounts.toml = await processTokenCount(toml)
        if (didCancel) return

        tokenCounts.CSV = await processTokenCount(csv)
        if (didCancel) return

        tokenCounts.toon = await processTokenCount(toon)
        if (didCancel) return

        const baseline = tokenCounts.minified

        const statsData = FORMAT_ORDER.map((key, index) => {
          const count = tokenCounts[key]
          const diff = count - baseline
          const percent = baseline === 0 ? 0 : (diff / baseline) * 100
          const isBaseline = index === 0
          return {
            format: key,
            count,
            diff,
            percent,
            isBaseline,
          }
        })

        startTransition(() => {
          setStats(statsData)
        })
      } catch (error) {
        console.error("Error processing formats:", error)
        if (!didCancel) {
          setFormats(null)
          setStats(null)
        }
      }
    }

    const timeoutId = setTimeout(processData, 0)

    return () => {
      didCancel = true
      clearTimeout(timeoutId)
    }
  }, [parsedData, isMounted])

  const activeFullscreenStats = React.useMemo(
    () => stats?.find((stat) => stat.format === fullscreenFormat),
    [stats, fullscreenFormat]
  )

  const loadTemplate = (key: TemplateKey) => {
    setInput(JSON.stringify(TEMPLATES[key], null, 2))
  }

  const isAlreadyPretty = React.useMemo(() => {
    try {
      const parsed = JSON.parse(input)
      const pretty = JSON.stringify(parsed, null, 2)
      return input.trim() === pretty
    } catch {
      return false
    }
  }, [input])

  const handlePrettify = () => {
    try {
      const parsed = JSON.parse(input)
      setInput(JSON.stringify(parsed, null, 2))
      setError(null)
    } catch {
      setError("Invalid JSON format")
    }
  }

  const handleCopy = React.useCallback(
    async (format: FormatType) => {
      if (!formats) return
      try {
        await navigator.clipboard.writeText(formats[format])
        setCopiedFormat(format)
        if (copyTimeout.current) {
          clearTimeout(copyTimeout.current)
        }
        copyTimeout.current = setTimeout(() => {
          setCopiedFormat(null)
        }, 1500)
      } catch (copyError) {
        console.error("Failed to copy format", copyError)
      }
    },
    [formats]
  )

  React.useEffect(
    () => () => {
      if (copyTimeout.current) {
        clearTimeout(copyTimeout.current)
      }
    },
    []
  )

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold">Input (JSON)</h2>
            <div className="hidden items-center gap-2 rounded-full border border-zinc-200 px-3 py-1.5 text-xs sm:flex dark:border-zinc-800">
              <Checkbox
                id="highlight-toggle"
                checked={highlightTokens}
                onCheckedChange={(checked) =>
                  setHighlightTokens(checked === true)
                }
                className="size-3.5 cursor-pointer"
              />
              <Label
                htmlFor="highlight-toggle"
                className="cursor-pointer text-xs font-medium text-zinc-600 dark:text-zinc-300"
              >
                Highlight tokens
              </Label>
            </div>
          </div>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              {Object.keys(TEMPLATES).map((key) => (
                <button
                  key={key}
                  onClick={() => loadTemplate(key as TemplateKey)}
                  className="bg-secondary cursor-pointer rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap capitalize transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                >
                  {key}
                </button>
              ))}
            </div>
            <div className="flex shrink-0 items-center gap-2 self-start rounded-full border border-zinc-200 px-3 py-1.5 text-xs sm:hidden dark:border-zinc-800">
              <Checkbox
                id="highlight-toggle-mobile"
                checked={highlightTokens}
                onCheckedChange={(checked) =>
                  setHighlightTokens(checked === true)
                }
                className="size-3.5 cursor-pointer"
              />
              <Label
                htmlFor="highlight-toggle-mobile"
                className="cursor-pointer text-xs font-medium text-zinc-600 dark:text-zinc-300"
              >
                Highlight tokens
              </Label>
            </div>
          </div>
        </div>

        <div className="relative">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className={cn(
              "h-[250px] resize-none text-sm sm:text-base",
              error
                ? "border-red-500 focus:ring-red-500/20"
                : "border-zinc-200 focus:ring-blue-500/20 dark:border-zinc-800"
            )}
            placeholder="Paste your JSON here..."
          />
          {error && (
            <div className="absolute right-4 bottom-4 left-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrettify}
            disabled={isAlreadyPretty || !!error}
            className="text-xs"
          >
            <Braces className="h-3.5 w-3.5" /> Prettify
          </Button>
        </div>
      </div>

      <div className="flex w-full flex-col gap-4">
        <h2 className="text-xl font-semibold">Converted Formats</h2>
        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {formats && !error ? (
            FORMAT_ORDER.map((format) => {
              const stat = stats?.find((s) => s.format === format)

              return (
                <div
                  key={format}
                  className="flex h-[250px] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm transition-all hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
                >
                  <div className="flex-1 overflow-y-auto bg-zinc-50 dark:bg-zinc-900/50">
                    <TokenViewer
                      text={formats[format]}
                      highlight={highlightTokens}
                      className="break-all whitespace-pre-wrap"
                    />
                  </div>
                  <div className="flex shrink-0 items-center justify-between gap-3 bg-white px-4 py-3 dark:bg-zinc-900">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFullscreenFormat(format)}
                        disabled={!formats}
                        className="h-7 w-7 p-0 sm:hidden"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                        <span className="sr-only">Fullscreen</span>
                      </Button>
                      <h3 className="font-semibold text-zinc-900 capitalize dark:text-zinc-100">
                        {format}
                      </h3>
                      {stat && (
                        <span className="bg-secondary rounded-full border border-zinc-200 px-2 py-0.5 text-xs font-medium text-green-600 dark:border-zinc-700 dark:bg-zinc-800">
                          {stat.count} tokens
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setFullscreenFormat(format)}
                        disabled={!formats}
                        className="hidden sm:flex"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                        <span className="sr-only">Fullscreen</span>
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopy(format)}
                        disabled={!formats}
                      >
                        {copiedFormat === format ? (
                          <>
                            <Check className="h-3.5 w-3.5" />
                            <span className="sr-only">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5" />
                            <span className="sr-only">Copy</span>
                          </>
                        )}
                      </Button>
                      {stat && (
                        <div className="text-xs font-medium">
                          {stat.isBaseline ? (
                            <span className="rounded-full border border-zinc-200 px-3 py-1 text-xs font-medium text-zinc-500 dark:border-zinc-700">
                              Baseline
                            </span>
                          ) : (
                            <span
                              className={cn(
                                "rounded-full border px-3 py-1 text-xs font-semibold",
                                stat.percent > 0
                                  ? "border-red-200 bg-red-50 text-red-600 dark:border-red-900/40 dark:bg-red-900/20"
                                  : "border-green-200 bg-green-50 text-green-600 dark:border-green-900/40 dark:bg-green-900/20"
                              )}
                            >
                              {stat.percent > 0 ? "+" : ""}
                              {stat.percent.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="col-span-full flex h-[300px] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-zinc-200 bg-linear-to-br from-zinc-50 to-white dark:border-zinc-800 dark:from-zinc-900 dark:to-black">
              {error ? (
                <>
                  <AlertCircle className="h-12 w-12 text-red-500" />
                  <p className="max-w-md text-center text-zinc-600 dark:text-zinc-400">
                    {error}
                  </p>
                </>
              ) : (
                <>
                  <div className="bg-primary flex h-16 w-16 items-center justify-center rounded-full">
                    <FileJson className="text-primary-foreground h-8 w-8" />
                  </div>
                  <div className="text-center">
                    <h3 className="mb-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                      Ready to analyze your data
                    </h3>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                      Enter your JSON data above to see token analysis across
                      different formats
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {fullscreenFormat && formats && (
        <DialogHelper
          title=""
          description=""
          open={Boolean(fullscreenFormat)}
          setOpen={(open) => {
            if (!open) {
              setFullscreenFormat(null)
            }
          }}
          className="max-w-7xl pt-0"
        >
          <div>
            <div className="flex flex-col gap-3 border-b border-zinc-100 py-4 dark:border-zinc-800">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs tracking-[0.3em] text-zinc-500 uppercase dark:text-zinc-400">
                    Format
                  </p>
                  <h3 className="dark:text-primary-foreground text-2xl font-semibold text-zinc-900 capitalize sm:text-3xl">
                    {fullscreenFormat}
                  </h3>
                </div>
                {isMounted && activeFullscreenStats && (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-green-600 sm:text-sm dark:border-zinc-700 dark:bg-zinc-800">
                      {activeFullscreenStats.count} tokens
                    </span>
                    {activeFullscreenStats.isBaseline ? (
                      <span className="rounded-full border border-zinc-200 px-3 py-1 text-xs font-medium text-zinc-500 sm:text-sm dark:border-zinc-700">
                        Baseline
                      </span>
                    ) : (
                      <span
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs font-semibold sm:text-sm",
                          activeFullscreenStats.percent > 0
                            ? "border-red-200 bg-red-50 text-red-600 dark:border-red-900/40 dark:bg-red-900/20"
                            : "border-green-200 bg-green-50 text-green-600 dark:border-green-900/40 dark:bg-green-900/20"
                        )}
                      >
                        {activeFullscreenStats.percent > 0 ? "+" : ""}
                        {activeFullscreenStats.percent.toFixed(1)}%
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto bg-zinc-50 p-4 dark:bg-zinc-900/40">
              <TokenViewer
                text={formats[fullscreenFormat]}
                highlight={highlightTokens}
                className="break-all whitespace-pre-wrap"
              />
            </div>
          </div>
        </DialogHelper>
      )}
    </div>
  )
}
